import { useState, useRef, useEffect, createContext, useContext, memo } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  TableHead,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { DatePicker } from '@/components/ui/date-picker';
import { SearchInput } from '@/components/ui/search-input';
import { ArrowUp, ArrowDown, X } from 'lucide-react';
import { toISODate } from '@/lib/dates';
import { cn } from "@/lib/utils";

// Context for shared column header state
export interface ColumnHeaderContextType {
  sortBy: string[];
  dateFrom: string | null;
  dateTo: string | null;
  onSort: (field: string, mode: 'asc' | 'desc' | 'add-asc' | 'add-desc' | 'remove') => void;
  onDateFilterChange: (dateFrom: string | null, dateTo: string | null) => void;
  onPageReset: () => void;
}

export const ColumnHeaderContext = createContext<ColumnHeaderContextType>(null!);

// Standalone sort icon helper
const getSortIcon = (field: string, sortBy: string[]) => {
  const index = sortBy.findIndex(s => s === field || s === `-${field}`);
  if (index === -1) return null;

  const isDesc = sortBy[index].startsWith('-');
  const sortNumber = sortBy.length > 1 ? index + 1 : null;

  return (
    <span className="flex items-center gap-0.5">
      {isDesc ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />}
      {sortNumber && (
        <span className="text-[10px] font-bold">{sortNumber}</span>
      )}
    </span>
  );
};

interface FilterableColumnHeaderProps {
  field: string;
  label: string;
  className?: string;
  filterType?: 'date' | 'multi-select' | 'boolean' | 'none';
  filterOptions?: { label: string; value: any }[];
  onFilterChange?: (values: any) => void;
  currentFilterValue?: any;
}

export const FilterableColumnHeader = memo(function FilterableColumnHeader({
  field,
  label,
  className,
  filterType = 'none',
  filterOptions = [],
  onFilterChange,
  currentFilterValue
}: FilterableColumnHeaderProps) {
  const { sortBy, dateFrom, dateTo, onSort, onDateFilterChange, onPageReset } = useContext(ColumnHeaderContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [tempFilterValue, setTempFilterValue] = useState<any>(currentFilterValue);
  const [tempDateFrom, setTempDateFrom] = useState<string | null>(null);
  const [tempDateTo, setTempDateTo] = useState<string | null>(null);
  const [sortedOptionsCache, setSortedOptionsCache] = useState<typeof filterOptions>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const hasActiveFilter = currentFilterValue !== null && currentFilterValue !== undefined &&
    (Array.isArray(currentFilterValue) ? currentFilterValue.length > 0 : true);
  const hasActiveSort = sortBy.some(s => s === field || s === `-${field}`);

  // When dropdown opens, initialize temp value and sort options once
  useEffect(() => {
    if (isOpen) {
      setTempFilterValue(currentFilterValue);

      // Sort options: selected items on top, then alphabetically
      const sorted = [...filterOptions].sort((a, b) => {
        const aSelected = currentFilterValue?.includes(a.value) || false;
        const bSelected = currentFilterValue?.includes(b.value) || false;

        // Selected items first
        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;

        // Then alphabetically
        return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
      });
      setSortedOptionsCache(sorted);
    }
  }, [isOpen]);

  const filteredOptions = filterType === 'multi-select' && searchQuery
    ? sortedOptionsCache.filter(opt => opt.label.toLowerCase().includes(searchQuery.toLowerCase()))
    : sortedOptionsCache;

  // Auto-focus search input when dropdown opens and initialize temp date values
  useEffect(() => {
    if (isOpen && filterType === 'multi-select' && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
    if (isOpen && filterType === 'date') {
      setTempDateFrom(dateFrom);
      setTempDateTo(dateTo);
    }
    if (isOpen) {
      setHighlightedIndex(0);
    }
  }, [isOpen, filterType, dateFrom, dateTo]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (filterType !== 'multi-select') return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.min(prev + 1, filteredOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
      e.preventDefault();
      const option = filteredOptions[highlightedIndex];
      const isSelected = tempFilterValue?.includes(option.value);
      const newValues = isSelected
        ? tempFilterValue.filter((v: any) => v !== option.value)
        : [...(tempFilterValue || []), option.value];
      setTempFilterValue(newValues);
    }
  };

  const selectAll = () => {
    const allValues = filteredOptions.map(opt => opt.value);
    setTempFilterValue(allValues);
  };

  const deselectAll = () => {
    setTempFilterValue([]);
  };

  return (
    <TableHead className={className}>
      <DropdownMenu open={isOpen} onOpenChange={(open) => {
        setIsOpen(open);
        // Apply filter changes and reset to page 1 when closing dropdown
        if (!open && filterType === 'multi-select' && onFilterChange) {
          // Only trigger API call if values actually changed
          const hasChanged = JSON.stringify(tempFilterValue) !== JSON.stringify(currentFilterValue);
          if (hasChanged) {
            onFilterChange(tempFilterValue);
            onPageReset();
          }
        }
        if (!open && filterType === 'date') {
          // Apply date filter changes only when dropdown closes
          const dateFromChanged = tempDateFrom !== dateFrom;
          const dateToChanged = tempDateTo !== dateTo;
          if (dateFromChanged || dateToChanged) {
            onDateFilterChange(tempDateFrom, tempDateTo);
            onPageReset();
          }
        }
      }}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "h-8 font-semibold hover:text-muted-foreground w-full justify-start px-2",
              (hasActiveFilter || hasActiveSort) && "text-primary"
            )}
          >
            <span className="flex items-center gap-1">
              {label}
              {getSortIcon(field, sortBy)}
              {hasActiveFilter && (
                <Badge variant="secondary" className="h-4 px-1 text-[10px] ml-1">
                  {Array.isArray(currentFilterValue) ? currentFilterValue.length : '✓'}
                </Badge>
              )}
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-auto min-w-56" onKeyDown={handleKeyDown}>
          {/* Sorting Options */}
          <DropdownMenuLabel className="text-xs">Sort</DropdownMenuLabel>
          <DropdownMenuItem onClick={(e) => {
            const mode = e.shiftKey ? 'add-asc' : 'asc';
            onSort(field, mode);
          }}>
            <ArrowUp className="h-4 w-4 mr-2" />
            Sort Ascending
          </DropdownMenuItem>
          <DropdownMenuItem onClick={(e) => {
            const mode = e.shiftKey ? 'add-desc' : 'desc';
            onSort(field, mode);
          }}>
            <ArrowDown className="h-4 w-4 mr-2" />
            Sort Descending
          </DropdownMenuItem>
          {sortBy.some(s => s === field || s === `-${field}`) && (
            <DropdownMenuItem onClick={() => onSort(field, 'remove')}>
              <X className="h-4 w-4 mr-2" />
              Remove Sort
            </DropdownMenuItem>
          )}

          {filterType !== 'none' && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs">Filter</DropdownMenuLabel>

              {/* Date Range Filter */}
              {filterType === 'date' && (
                <div
                  className="p-2 space-y-2 w-[300px]"
                  // Stop keys from bubbling to DropdownMenuContent — Radix
                  // typeahead would otherwise steal focus from the date
                  // pickers' text inputs (same fix as the multi-select below).
                  onKeyDown={(e) => e.stopPropagation()}
                  onKeyUp={(e) => e.stopPropagation()}
                >
                  {/* From Date */}
                  <div>
                    <Label className="text-xs mb-1.5 block">From</Label>
                    <DatePicker
                      value={tempDateFrom}
                      onChange={(iso) => setTempDateFrom(iso || null)}
                      className="h-8"
                    />
                    <div className="flex flex-wrap gap-2 mt-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => setTempDateFrom(toISODate(new Date()))}
                      >
                        Today
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => {
                          const today = new Date();
                          setTempDateFrom(toISODate(new Date(today.getFullYear(), today.getMonth(), 1)));
                        }}
                      >
                        Start of Month
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => setTempDateFrom(toISODate(new Date(new Date().getFullYear(), 0, 1)))}
                      >
                        Start of Year
                      </Button>
                    </div>
                  </div>

                  {/* To Date */}
                  <div>
                    <Label className="text-xs mb-1.5 block">To</Label>
                    <DatePicker
                      value={tempDateTo}
                      onChange={(iso) => setTempDateTo(iso || null)}
                      minDate={tempDateFrom || dateFrom || undefined}
                      className="h-8"
                    />
                    <div className="flex flex-wrap gap-2 mt-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => setTempDateTo(toISODate(new Date()))}
                      >
                        Today
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => {
                          const today = new Date();
                          setTempDateTo(toISODate(new Date(today.getFullYear(), today.getMonth() + 1, 0)));
                        }}
                      >
                        End of Month
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => setTempDateTo(toISODate(new Date(new Date().getFullYear(), 11, 31)))}
                      >
                        End of Year
                      </Button>
                    </div>
                  </div>

                  {/* Quick Range Presets */}
                  <div className="pt-1">
                    <Label className="text-xs mb-1.5 block">Quick Ranges</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs w-full"
                        onClick={() => {
                          const today = new Date();
                          setTempDateFrom(toISODate(new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)));
                          setTempDateTo(toISODate(today));
                        }}
                      >
                        Last 7 Days
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs w-full"
                        onClick={() => {
                          const today = new Date();
                          setTempDateFrom(toISODate(new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)));
                          setTempDateTo(toISODate(today));
                        }}
                      >
                        Last 30 Days
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs w-full"
                        onClick={() => {
                          const today = new Date();
                          setTempDateFrom(toISODate(new Date(today.getFullYear(), today.getMonth(), 1)));
                          setTempDateTo(toISODate(new Date(today.getFullYear(), today.getMonth() + 1, 0)));
                        }}
                      >
                        This Month
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs w-full"
                        onClick={() => {
                          const today = new Date();
                          setTempDateFrom(toISODate(new Date(today.getFullYear(), today.getMonth() - 1, 1)));
                          setTempDateTo(toISODate(new Date(today.getFullYear(), today.getMonth(), 0)));
                        }}
                      >
                        Last Month
                      </Button>
                    </div>
                  </div>

                  <div className="flex gap-2 mt-2">
                    <Button
                      variant="default"
                      size="sm"
                      className="flex-1 h-7"
                      onClick={() => {
                        // Apply the temporary date filters
                        const dateFromChanged = tempDateFrom !== dateFrom;
                        const dateToChanged = tempDateTo !== dateTo;
                        if (dateFromChanged || dateToChanged) {
                          onDateFilterChange(tempDateFrom, tempDateTo);
                          onPageReset();
                        }
                        setIsOpen(false);
                      }}
                    >
                      Apply
                    </Button>
                    {((isOpen ? tempDateFrom : dateFrom) || (isOpen ? tempDateTo : dateTo)) && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 px-2"
                        onClick={() => {
                          setTempDateFrom(null);
                          setTempDateTo(null);
                        }}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Multi-Select Filter */}
              {filterType === 'multi-select' && (
                <>
                  <div
                    className="p-2"
                    // Stop every key from bubbling to DropdownMenuContent.
                    // Radix DropdownMenu runs typeahead on bubbled keydowns
                    // and steals focus to the "matching" menu item, which
                    // is what kicks the user out of the search input after
                    // every letter. We still handle arrow/enter explicitly
                    // in handleKeyDown on the input itself.
                    onKeyDown={(e) => e.stopPropagation()}
                    onKeyUp={(e) => e.stopPropagation()}
                  >
                    <SearchInput
                      ref={searchInputRef}
                      placeholder="Type to search..."
                      value={searchQuery}
                      onChange={(value) => {
                        setSearchQuery(value);
                        setHighlightedIndex(0);
                      }}
                      onKeyDown={handleKeyDown}
                      autoFocus
                    />
                  </div>

                  {/* Select All / Deselect All Buttons */}
                  <div className="flex gap-1 px-2 pb-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 flex-1 text-xs"
                      onClick={selectAll}
                    >
                      Select All
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 flex-1 text-xs"
                      onClick={deselectAll}
                    >
                      Deselect All
                    </Button>
                  </div>

                  {/* Apply Button */}
                  <div className="px-2 pb-2">
                    <Button
                      variant="default"
                      size="sm"
                      className="w-full h-7"
                      onClick={() => {
                        // Apply the temporary filter values
                        if (onFilterChange) {
                          const hasChanged = JSON.stringify(tempFilterValue) !== JSON.stringify(currentFilterValue);
                          if (hasChanged) {
                            onFilterChange(tempFilterValue);
                            onPageReset();
                          }
                        }
                        setIsOpen(false);
                      }}
                    >
                      Apply
                    </Button>
                  </div>

                  <ScrollArea className="h-48">
                    {filteredOptions.map((option, index) => (
                      <DropdownMenuCheckboxItem
                        key={option.value}
                        checked={tempFilterValue?.includes(option.value)}
                        onCheckedChange={(checked) => {
                          const newValues = checked
                            ? [...(tempFilterValue || []), option.value]
                            : (tempFilterValue || []).filter((v: any) => v !== option.value);
                          setTempFilterValue(newValues);
                        }}
                        onSelect={(e) => e.preventDefault()}
                        className={cn(
                          highlightedIndex === index && "bg-accent"
                        )}
                        onMouseEnter={() => setHighlightedIndex(index)}
                      >
                        {option.label}
                      </DropdownMenuCheckboxItem>
                    ))}
                    {filteredOptions.length === 0 && (
                      <div className="p-4 text-center text-sm text-muted-foreground">
                        No results found
                      </div>
                    )}
                  </ScrollArea>
                  {tempFilterValue?.length > 0 && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => {
                          setTempFilterValue([]);
                        }}
                      >
                        <X className="h-4 w-4 mr-2" />
                        Clear Selection
                      </DropdownMenuItem>
                    </>
                  )}
                </>
              )}

              {/* Boolean Filter */}
              {filterType === 'boolean' && (
                <>
                  <DropdownMenuCheckboxItem
                    checked={currentFilterValue === true}
                    onCheckedChange={(checked) => {
                      if (onFilterChange) onFilterChange(checked ? true : null);
                      onPageReset();
                    }}
                  >
                    Billable
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={currentFilterValue === false}
                    onCheckedChange={(checked) => {
                      if (onFilterChange) onFilterChange(checked ? false : null);
                      onPageReset();
                    }}
                  >
                    Non-Billable
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuSeparator />
                  <div className="p-2">
                    <Button
                      variant="default"
                      size="sm"
                      className="w-full h-7"
                      onClick={() => {
                        // Boolean filters apply immediately, so just close
                        setIsOpen(false);
                      }}
                    >
                      Apply
                    </Button>
                  </div>
                  {currentFilterValue !== null && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => {
                          if (onFilterChange) onFilterChange(null);
                          onPageReset();
                        }}
                      >
                        <X className="h-4 w-4 mr-2" />
                        Clear Filter
                      </DropdownMenuItem>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </TableHead>
  );
});
